import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

interface ActionCompletedIconProps {
    /**
     * Whether the action has been completed (e.g. website visited,
     * enquiry submitted). When true, shows an animated tick instead of
     * the default icon.
     */
    isCompleted: boolean;
    /** The icon to show before the action is completed. */
    defaultIcon: ReactNode;
    /** Additional CSS classes applied to the tick icon. */
    className?: string;
}

/**
 * Swaps a default icon for an animated tick (checkmark) once an action
 * is completed. Used on "Visit Website" and enquiry CTA buttons to give
 * the user satisfying visual feedback.
 */
const ActionCompletedIcon = ({
    isCompleted,
    defaultIcon,
    className,
}: ActionCompletedIconProps) => {
    const [hasAnimated, setHasAnimated] = useState(false);

    if (!isCompleted) {
        return <>{defaultIcon}</>;
    }

    return (
        <Check
            className={cn(
                'text-green-600 inline-block',
                !hasAnimated && 'animate-tick-pop-in',
                className,
            )}
            onAnimationEnd={() => setHasAnimated(true)}
        />
    );
};

export default ActionCompletedIcon;
